/** Post-process imported inline HTML for TipTap compatibility. */

const NOISE_FONT_FAMILIES = /^(calibri|cambria|arial|helvetica|sans-serif|serif)$/i;

export interface InlineNormalizeOptions {
  preserveColors?: boolean;
  preserveFonts?: boolean;
}

function styleBoldItalicUnderline(el: HTMLElement): boolean {
  let changed = false;
  const weight = el.style.fontWeight?.toLowerCase();
  if (weight === "bold" || weight === "700" || parseInt(weight || "0", 10) >= 600) {
    const strong = el.ownerDocument.createElement("strong");
    strong.innerHTML = el.innerHTML;
    el.innerHTML = "";
    el.style.fontWeight = "";
    el.appendChild(strong);
    changed = true;
  }
  if (el.style.fontStyle === "italic") {
    wrapChildrenInTag(el, "em");
    el.style.fontStyle = "";
    changed = true;
  }
  const deco = el.style.textDecoration?.toLowerCase() ?? "";
  if (deco.includes("underline")) {
    wrapChildrenInTag(el, "u");
    el.style.textDecoration = deco.replace("underline", "").trim();
    if (!el.style.textDecoration) el.removeAttribute("style");
    changed = true;
  }
  if (deco.includes("line-through")) {
    wrapChildrenInTag(el, "s");
    changed = true;
  }
  return changed;
}

function styleSupSub(el: HTMLElement): boolean {
  const align = el.style.verticalAlign?.toLowerCase() ?? "";
  if (align === "super" || align === "superscript") {
    wrapChildrenInTag(el, "sup");
    el.style.verticalAlign = "";
    if (!el.style.cssText.trim()) el.removeAttribute("style");
    return true;
  }
  if (align === "sub" || align === "subscript") {
    wrapChildrenInTag(el, "sub");
    el.style.verticalAlign = "";
    if (!el.style.cssText.trim()) el.removeAttribute("style");
    return true;
  }
  return false;
}

function wrapChildrenInTag(el: HTMLElement, tag: string): void {
  const wrapper = el.ownerDocument.createElement(tag);
  while (el.firstChild) wrapper.appendChild(el.firstChild);
  el.appendChild(wrapper);
}

export function normalizeSemanticInlineTags(root: ParentNode): number {
  let count = 0;
  const replacements: [string, string][] = [
    ["b", "strong"],
    ["i", "em"],
    ["strike", "s"],
  ];

  for (const [from, to] of replacements) {
    root.querySelectorAll(from).forEach((node) => {
      if (!(node instanceof HTMLElement) || node.tagName.toLowerCase() !== from) {
        return;
      }
      const replacement = node.ownerDocument.createElement(to);
      replacement.innerHTML = node.innerHTML;
      for (const attr of Array.from(node.attributes)) {
        replacement.setAttribute(attr.name, attr.value);
      }
      node.parentElement?.replaceChild(replacement, node);
      count++;
    });
  }

  return count;
}

export function cleanInlineSpanStyles(
  root: ParentNode,
  options: InlineNormalizeOptions = {},
): number {
  let count = 0;
  root.querySelectorAll("span[style]").forEach((node) => {
    if (!(node instanceof HTMLElement)) return;

    if (styleBoldItalicUnderline(node)) count++;
    if (styleSupSub(node)) count++;

    if (!options.preserveFonts) {
      const fontFamily = node.style.fontFamily
        .replace(/['"]/g, "")
        .split(",")[0]
        ?.trim();
      if (fontFamily && NOISE_FONT_FAMILIES.test(fontFamily)) {
        node.style.fontFamily = "";
        count++;
      }

      if (node.style.fontSize) {
        const pt = parseFloat(node.style.fontSize);
        if (
          !Number.isNaN(pt) &&
          Math.abs(pt - 13) < 0.2 &&
          node.style.fontSize.endsWith("pt")
        ) {
          node.style.fontSize = "";
          count++;
        }
      }
    }

    if (!options.preserveColors && node.style.color) {
      node.style.color = "";
      count++;
    }

    if (!node.style.cssText.trim() && node.parentElement) {
      while (node.firstChild) node.parentElement.insertBefore(node.firstChild, node);
      node.remove();
      count++;
    }
  });

  return count;
}

export function normalizeImportedInlineHtml(
  root: ParentNode,
  options: InlineNormalizeOptions = {},
): {
  semanticTagsNormalized: number;
  spansCleaned: number;
} {
  return {
    semanticTagsNormalized: normalizeSemanticInlineTags(root),
    spansCleaned: cleanInlineSpanStyles(root, options),
  };
}
